import { Navigate } from 'react-router-dom';
import { useAppContext } from '../../store/AppContext';

interface AdminProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * AdminProtectedRoute component ensures only admin users can access admin screens.
 * 
 * Security Requirements:
 * - Server-side authorization check (profile fetched in AppContext from Firestore)
 * - Blocks non-admin users from accessing admin routes
 * - Redirects to home with error message if unauthorized
 * 
 * This is a CRITICAL security component - UI hiding alone is not sufficient.
 * Admin status is determined by AppContext's single profile fetch (backend truth).
 */
export function AdminProtectedRoute({ children }: AdminProtectedRouteProps) {
  const { user, loading: authLoading, authError, profileSettled, isAdmin } = useAppContext();

  // Show loading state while checking auth or profile (which includes admin status)
  if (authLoading || !profileSettled) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white">
        <div className="text-center">
          <p className="text-sm text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user || authError) {
    return <Navigate to="/auth/login" replace />;
  }

  // Block access if not admin - redirect to home with message
  if (!isAdmin) {
    // Store error message in sessionStorage to display on home screen
    try {
      sessionStorage.setItem('adminAccessDenied', 'true');
    } catch (error) {
      console.error('Failed to store access denied message:', error);
    }
    
    // Redirect to home
    return <Navigate to="/home" replace />;
  }

  // Allow access — admin confirmed from backend via AppContext
  return <>{children}</>;
}
